import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { RECORD_STATUSES, TOPUP_PAYMENT_TYPES, TOPUP_TYPES, topupInputSchema } from '@app/shared';
import type { TopupDto, TopupInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import {
  MoneyInput,
  MultiSelectInput,
  NumberInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { useCreateTopup, useUpdateTopup } from './api';
import { useCarriers } from './lookups';
import { STATE_OPTIONS, carrierOptions, withCurrent } from './options';

interface Props {
  open: boolean;
  /** Present when editing. */
  topup?: TopupDto;
  onClose: () => void;
}

/**
 * Add or edit a top-up.
 * Assumption: the reference's add-topup form was not observed, so the fields are the
 * columns of the reference's top-up list.
 */
export function TopupFormDialog({ open, topup, onClose }: Props) {
  const notify = useNotify();
  const carriers = useCarriers(open);
  const createTopup = useCreateTopup();
  const updateTopup = useUpdateTopup();

  const { control, handleSubmit, reset, setError } = useForm<TopupInput>({
    resolver: zodResolver(topupInputSchema),
  });

  useEffect(() => {
    if (!open) return;
    reset(
      topup
        ? {
            name: topup.name,
            carrierId: topup.carrierId,
            paymentType: topup.paymentType,
            wholesaleCode: topup.wholesaleCode,
            type: topup.type,
            costCents: topup.costCents,
            voiceMinutes: topup.voiceMinutes,
            textCount: topup.textCount,
            dataMb: topup.dataMb,
            status: topup.status,
            states: topup.states,
          }
        : {
            name: '',
            paymentType: 'Credit Card',
            wholesaleCode: '',
            type: 'TOPUP',
            voiceMinutes: 0,
            textCount: 0,
            dataMb: 0,
            status: 'Active',
            states: [],
          },
    );
  }, [open, topup, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      if (topup) {
        await updateTopup.mutateAsync({ id: topup.id, input: values });
        notify.success('Topup updated.');
      } else {
        await createTopup.mutateAsync(values);
        notify.success('Topup created.');
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={topup ? 'Edit Topup' : 'Add Topup'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createTopup.isPending || updateTopup.isPending}
      maxWidth="md"
    >
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}
      >
        <TextInput control={control} name="name" label="Topup Name" required />
        <SelectInput
          control={control}
          name="carrierId"
          label="Carrier"
          required
          options={withCurrent(
            carrierOptions(carriers.data),
            topup ? { value: topup.carrierId, label: topup.carrier.code } : undefined,
          )}
        />
        <SelectInput
          control={control}
          name="paymentType"
          label="Payment Type"
          options={toOptions(TOPUP_PAYMENT_TYPES)}
        />
        <TextInput control={control} name="wholesaleCode" label="Wholesale Topup Code" />
        <SelectInput control={control} name="type" label="Type" options={toOptions(TOPUP_TYPES)} />
        <MoneyInput control={control} name="costCents" label="Topup Cost" required />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
        <NumberInput control={control} name="voiceMinutes" label="Voice(Minutes)" emptyValue={0} />
        <NumberInput control={control} name="textCount" label="Text" emptyValue={0} />
        <NumberInput control={control} name="dataMb" label="Data(MB)" emptyValue={0} />
        <Box sx={{ gridColumn: '1 / -1' }}>
          <MultiSelectInput
            control={control}
            name="states"
            label="State"
            required
            options={STATE_OPTIONS}
          />
        </Box>
      </Box>
    </FormDialog>
  );
}
